import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  type TaxFormPayload, type TaxFormType, type TaxProfileSummary,
  saveTaxDraft, submitTaxForm,
} from "@/lib/affiliateTax";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: TaxProfileSummary | null;
  onSubmitted: () => void;
};

const CLASSIFICATIONS: Array<{ value: string; label: string }> = [
  { value: "individual", label: "Individual / Sole proprietor" },
  { value: "sole_proprietor", label: "Sole proprietor (separate)" },
  { value: "llc_single", label: "Single-member LLC (disregarded)" },
  { value: "llc_c", label: "LLC — C corporation" },
  { value: "llc_s", label: "LLC — S corporation" },
  { value: "llc_p", label: "LLC — Partnership" },
  { value: "c_corp", label: "C corporation" },
  { value: "s_corp", label: "S corporation" },
  { value: "partnership", label: "Partnership" },
  { value: "trust", label: "Trust / estate" },
  { value: "other", label: "Other" },
];

export function TaxFormWizard({ open, onOpenChange, existing, onSubmitted }: Props) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [certify, setCertify] = useState(false);

  const [country, setCountry] = useState(existing?.country ?? "");
  const [formType, setFormType] = useState<TaxFormType | null>(existing?.form_type ?? null);
  const [legalName, setLegalName] = useState(existing?.legal_name ?? "");
  const [businessName, setBusinessName] = useState(existing?.business_name ?? "");
  const [classification, setClassification] = useState(existing?.tax_classification ?? "individual");

  const [addr1, setAddr1] = useState("");
  const [addr2, setAddr2] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [postal, setPostal] = useState("");

  const [tinKind, setTinKind] = useState<"ssn" | "ein" | "foreign">(existing?.tin_kind ?? "ssn");
  const [tin, setTin] = useState("");
  const [treatyCountry, setTreatyCountry] = useState(existing?.treaty_country ?? "");
  const [signedName, setSignedName] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0); setCertify(false); setTin(""); setSignedName("");
    }
  }, [open]);

  const onCountryNext = () => {
    if (!country || country.length !== 2) return toast.error("Enter a 2-letter ISO country code");
    const ft: TaxFormType = country.toUpperCase() === "US" ? "w9" : "w8ben";
    setFormType(ft);
    if (ft === "w8ben") setTinKind("foreign");
    setStep(1);
  };

  const buildPayload = (): TaxFormPayload => ({
    form_type: formType!,
    legal_name: legalName.trim(),
    business_name: businessName.trim() || undefined,
    country: country.toUpperCase(),
    tax_classification: formType === "w9" ? classification : undefined,
    tin_kind: tinKind,
    tin: tin.trim() || undefined,
    address_line1: addr1.trim(),
    address_line2: addr2.trim() || undefined,
    address_city: city.trim(),
    address_region: region.trim() || undefined,
    address_postal: postal.trim() || undefined,
    treaty_country: formType === "w8ben" ? treatyCountry.toUpperCase() : undefined,
    signed_name: signedName.trim() || undefined,
  });

  const onSaveDraft = async () => {
    if (!formType) return;
    setSaving(true);
    try {
      await saveTaxDraft(buildPayload());
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally { setSaving(false); }
  };

  const onSubmit = async () => {
    if (!certify) return toast.error("Please certify before submitting");
    if (!signedName.trim()) return toast.error("Type your full legal name to sign");
    setSubmitting(true);
    try {
      await submitTaxForm(buildPayload());
      toast.success("Tax form submitted for review");
      onSubmitted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {formType === "w9" ? "W-9 — US person" : formType === "w8ben" ? "W-8BEN — non-US person" : "Tax information"}
          </DialogTitle>
          <DialogDescription>
            Required before any commission payouts. Your TIN and address are encrypted at rest.
          </DialogDescription>
        </DialogHeader>

        {step === 0 ? (
          <div className="space-y-3">
            <Label htmlFor="country">Country of tax residence (ISO-2)</Label>
            <Input id="country" maxLength={2} value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              placeholder="US, GB, DE, …" />
            <p className="text-xs text-muted-foreground">
              US residents complete a W-9. All others complete a W-8BEN.
            </p>
            <DialogFooter>
              <Button onClick={onCountryNext}>Continue</Button>
            </DialogFooter>
          </div>
        ) : step === 1 ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="ln">Legal name</Label>
              <Input id="ln" value={legalName} onChange={(e) => setLegalName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bn">Business name (if different)</Label>
              <Input id="bn" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
            </div>
            {formType === "w9" ? (
              <div className="space-y-1">
                <Label>Federal tax classification</Label>
                <Select value={classification} onValueChange={setClassification}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-2">
              <Input placeholder="Address line 1" value={addr1} onChange={(e) => setAddr1(e.target.value)} />
              <Input placeholder="Address line 2 (optional)" value={addr2} onChange={(e) => setAddr2(e.target.value)} />
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
                <Input placeholder="State / region" value={region} onChange={(e) => setRegion(e.target.value)} />
                <Input placeholder="Postal" value={postal} onChange={(e) => setPostal(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button onClick={() => setStep(2)}>Continue</Button>
            </DialogFooter>
          </div>
        ) : step === 2 ? (
          <div className="space-y-3">
            {formType === "w9" ? (
              <div className="space-y-1">
                <Label>TIN type</Label>
                <Select value={tinKind} onValueChange={(v) => setTinKind(v as "ssn" | "ein")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ssn">SSN</SelectItem>
                    <SelectItem value="ein">EIN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Treaty country (if claiming benefits)</Label>
                <Input maxLength={2} value={treatyCountry}
                  onChange={(e) => setTreatyCountry(e.target.value.toUpperCase())}
                  placeholder="Optional" />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="tin">
                {tinKind === "ssn" ? "SSN" : tinKind === "ein" ? "EIN" : "Foreign TIN"}
              </Label>
              <Input id="tin" value={tin} onChange={(e) => setTin(e.target.value)}
                placeholder={tinKind === "ssn" ? "123-45-6789" : tinKind === "ein" ? "12-3456789" : "Foreign tax ID"} />
              <p className="text-xs text-muted-foreground">
                Stored encrypted. Only the last 4 digits will be shown back to you.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button variant="ghost" onClick={onSaveDraft} disabled={saving}>
                {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Save draft
              </Button>
              <Button onClick={() => setStep(3)}>Continue to sign</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              By signing below you certify under penalties of perjury that the information you've
              provided is true, correct, and complete. Your IP address and timestamp will be
              recorded as part of the e-signature.
            </p>
            <div className="space-y-1">
              <Label htmlFor="sig">Type your full legal name to sign</Label>
              <Input id="sig" value={signedName} onChange={(e) => setSignedName(e.target.value)} />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={certify} onCheckedChange={(v) => setCertify(v === true)} />
              <span>I certify the information above is accurate and I am the person named.</span>
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button onClick={onSubmit} disabled={submitting || !certify}>
                {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Submit for review
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
